import buildInfo from "../buildId.json";

/**
 * Version guard: detect when this tab is running a stale cached bundle and
 * hard-reload onto the freshly deployed one.
 *
 * How: the bundle carries its own build id (src/buildId.json, stamped by
 * scripts/write-build-id.js). At runtime we fetch /meta.json — deployed next
 * to index.html and served with Cache-Control: no-store — and compare. On
 * mismatch we re-fetch index.html with cache:"reload", which replaces the
 * browser's cached HTTP entry with a fresh copy, then reload the page.
 *
 * Fail-open by design: any network/parse error resolves false and never
 * blocks the app. A sessionStorage flag prevents reload loops when a
 * misbehaving proxy keeps serving a mismatched meta.json.
 */

const RELOADED_FLAG = "version_guard_reloaded";
const PUBLIC_BASE = process.env.PUBLIC_URL || "";

/**
 * Compare the compiled build id against the server's meta.json.
 * @param {{timeoutMs?: number}} [opts] abort the check after timeoutMs (used
 *   on the login click so auth is never delayed by a slow network).
 * @returns {Promise<boolean>} true when a reload has been triggered — the
 *   caller should stop what it was doing.
 */
export async function checkBundleVersion(opts = {}) {
  try {
    const controller = new AbortController();
    const timer = opts.timeoutMs
      ? setTimeout(() => controller.abort(), opts.timeoutMs)
      : null;
    const res = await fetch(`${PUBLIC_BASE}/meta.json`, {
      cache: "no-store",
      signal: controller.signal,
    });
    if (timer) clearTimeout(timer);
    if (!res.ok) return false;
    const meta = await res.json();
    const latest = meta && meta.version;
    if (!latest || latest === buildInfo.version) return false;
    if (sessionStorage.getItem(RELOADED_FLAG) === latest) return false;
    sessionStorage.setItem(RELOADED_FLAG, latest);
    // Overwrite the cached index.html with a fresh copy, then reload onto it.
    await fetch(`${PUBLIC_BASE}/index.html`, { cache: "reload" });
    window.location.reload();
    return true;
  } catch (_) {
    return false;
  }
}

/** App-start hook: check now, and again whenever the tab becomes visible. */
export function initVersionGuard() {
  checkBundleVersion();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkBundleVersion();
  });
}
